<template>
    <div>
        <!-- Column headers -->
        <div class="pay-in__payment-row pay-in__payment-row--header mb-1">
            <label class="pay-in__col-method">Select payment method</label>
            <label v-if="showToPay" class="pay-in__col-topay">To pay <span class="pay-in__required">*</span></label>
            <label class="pay-in__col-received">Amount received <span class="pay-in__required">*</span></label>
            <label class="pay-in__col-change">Change</label>
            <label class="pay-in__col-agent">Agent</label>
            <div class="pay-in__col-delete"></div>
        </div>

        <!-- Payment rows -->
        <div
            v-for="(payment, index) in payments"
            :key="index"
            class="pay-in__payment-row mb-2"
        >
            <div class="pay-in__col-method">
                <div :class="{ 'pay-in__select-wrapper--covered': isAmountCovered && !payment.amountReceived }">
                <v-select
                    :options="getAvailableMethodsFor(index)"
                    label="description"
                    :value="getSelectedMethodFor(index)"
                    placeholder="Select method"
                    :disabled="!!(payment.id || payment.allocationId)"
                    @input="onMethodChange(index, $event)"
                >
                    <template #option="option">
                        <span>{{ option.description }}</span>
                    </template>
                    <template #selected-option="option">
                        <span>{{ option.description }}</span>
                    </template>
                </v-select>
                </div>
            </div>

            <div v-if="showToPay" class="pay-in__col-topay pay-in__input-group">
                <input
                    type="text"
                    v-model="payment.toPay"
                    v-numFloat
                    :class="{ 'pay-in__input--covered': isAmountCovered && !payment.amountReceived }"
                    :disabled="payment.id || payment.allocationId"
                    @input="onToPayChange(index)"
                />
                <span class="pay-in__input-suffix">€</span>
            </div>

            <div class="pay-in__col-received pay-in__input-group">
                <input
                    type="text"
                    v-model="payment.amountReceived"
                    v-numFloat
                    :disabled="
                        payment.id ||
                        payment.allocationId ||
                        (
                            showToPay &&
                            amountReceivedDisabled &&
                            payment.type !== CASH_PAYMENT_TYPE_ID
                        )
                    "
                    :class="{ 'pay-in__input--covered': isAmountCovered && !payment.amountReceived }"
                    @input="onAmountReceivedChange(index)"
                    @blur="formatAmountReceived(index)"
                />
                <span class="pay-in__input-suffix">€</span>
            </div>

            <div class="pay-in__col-change pay-in__input-group">
                <template v-if="payment.type === CASH_PAYMENT_TYPE_ID">
                    <input type="text" :value="calculateChange(payment)" disabled />
                    <span class="pay-in__input-suffix">€</span>
                </template>
            </div>

            <div class="pay-in__col-agent">
                <input
                    type="checkbox"
                    :checked="isAgentPayment(payment)"
                    :disabled="isAgentCheckboxDisabled(payment)"
                    @change="onAgentPaymentChange(index, $event.target.checked)"
                />
            </div>

            <div class="pay-in__col-delete">
                <i
                    v-if="isPaymentDeletable(payment)"
                    class="ico ico-delete dark pointer pay-in__delete-icon"
                    @click="removePaymentLine(index)"
                ></i>
            </div>
        </div>

        <!-- Add payment method -->
        <div class="mt-2 mb-3">
            <span
                class="pay-in__add-method"
                :class="{ 'pay-in__add-method--disabled': isAmountCovered || noMethodsRemaining }"
                @click="addPaymentLine"
            >
                <i class="ico-add fs-smallest add-circle color-1 mr-1"></i> Add payment method
            </span>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import extendedVSelect from '@/extends/vue-select'
import { CASH_PAYMENT_TYPE_ID, AGENT_DEPOSIT_PAYMENT_TYPE_ID } from '@/tools/modal/ReservationPayIn/payInHelpers'

export default {
    components: { vSelect: extendedVSelect },
    props: {
        payments: {
            type: Array,
            required: true,
        },
        isAmountCovered: {
            type: Boolean,
            default: false,
        },
        showToPay: {
            type: Boolean,
            default: false,
        },
        remainingToPay: {
            type: Number,
            default: 0,
        },
        amountDue: {
            type: Number,
            default: 0,
        },
        amountReceivedDisabled: {
            type: Boolean,
            default: false,
        },
        allowEmpty: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return { CASH_PAYMENT_TYPE_ID, AGENT_DEPOSIT_PAYMENT_TYPE_ID }
    },
    computed: {
        ...mapGetters(['getPaymentTypeListAll']),
        totalReceived() {
            return this.payments.reduce((sum, payment) => {
                const received = parseFloat(payment.amountReceived) || 0
                const change = parseFloat(payment.change) || 0

                return sum + (received - change)
            }, 0)
        },
        noMethodsRemaining() {
            const usedTypes = this.payments.map(payment => payment.type)
            return !this.getPaymentTypeListAll.some(method => !usedTypes.includes(method.id))
        },
    },
    methods: {
        formatAmountReceived(index) {
            const payment = this.payments[index]

            if (payment.amountReceived === null || payment.amountReceived === '') {
                return
            }

            const normalized = payment.amountReceived.toString().replace(',', '.')
            let value = parseFloat(normalized)

            if (isNaN(value)) {
                return
            }

            if (this.showToPay) {
                const toPay = parseFloat(
                    payment.toPay
                        ? payment.toPay.toString().replace(',', '.')
                        : 0
                ) || 0

                // For ALL payment methods:
                // amountReceived cannot be smaller than toPay
                if (value < toPay) {
                    value = toPay
                }
            }

            this.$set(
                this.payments[index],
                'amountReceived',
                value.toFixed(2)
            )
        },
        onAmountReceivedChange(index) {
            const payment = this.payments[index]
            const input = payment.amountReceived
            
            if (input !== null && input !== '') {
                const decimalIndex = input.length - 1
                const parts = input.toString().split(/[.,]/)
                if (parts.length > 1 && parts[1].length > 2) {
                    const separator = input.includes('.') ? '.' : ','
                    const truncated = input.substring(0, input.lastIndexOf(separator) + 3)
                    this.$set(this.payments[index], 'amountReceived', truncated)
                    return
                }
            }
            
            if (this.showToPay) {
                if (payment.type !== CASH_PAYMENT_TYPE_ID) {
                    const toPay = parseFloat(payment.toPay.toString().replace(',', '.')) || 0
                    const received = parseFloat(input ? input.toString().replace(',', '.') : 0) || 0
                    if (received > toPay) {
                        this.$set(this.payments[index], 'amountReceived', toPay.toString().replace('.', ','))
                    }
                }
                return
            }
            if (payment.type === CASH_PAYMENT_TYPE_ID) return
            const excess = this.totalReceived - this.remainingToPay
            if (excess > 0) {
                const current = parseFloat(input ? input.toString().replace(',', '.') : 0) || 0
                this.$set(this.payments[index], 'amountReceived', parseFloat(Math.max(0, current - excess).toFixed(2)))
            }
        },
        onToPayChange(index) {
            const otherTotal = this.payments
                .filter((_, i) => i !== index)
                .reduce((sum, p) => sum + (parseFloat(p.toPay) || 0), 0)

            const max = this.amountDue - otherTotal
            const value = parseFloat(this.payments[index].toPay) || 0

            if (value > max) {
                this.$set(this.payments[index], 'toPay', max > 0 ? max : 0)
            }

            const payment = this.payments[index]

            // Auto-sync amount received with toPay
            if (
                this.showToPay &&
                this.amountReceivedDisabled &&
                payment.type !== CASH_PAYMENT_TYPE_ID
            ) {
                this.$set(
                    this.payments[index],
                    'amountReceived',
                    payment.toPay
                )
            }

            // Cash: toPay cannot exceed the amount actually received
            if (
                this.showToPay &&
                payment.type === CASH_PAYMENT_TYPE_ID &&
                payment.amountReceived !== null &&
                payment.amountReceived !== ''
            ) {
                const received = parseFloat(payment.amountReceived.toString().replace(',', '.')) || 0
                const current = parseFloat(payment.toPay) || 0
                if (current > received) {
                    this.$set(this.payments[index], 'toPay', received)
                }
            }
        },
        getAvailableMethodsFor(index) {
            const usedTypes = this.payments
                .filter((_, i) => i !== index)
                .map(payment => payment.type)
            return this.getPaymentTypeListAll.filter(method => !usedTypes.includes(method.id))
        },
        getSelectedMethodFor(index) {
            return this.getPaymentTypeListAll.find(method => method.id === this.payments[index].type) || null
        },
        onMethodChange(index, method) {
            if (!method) return

            this.$set(this.payments[index], 'type', method.id)

            const payment = this.payments[index]

            if (
                this.showToPay &&
                this.amountReceivedDisabled &&
                payment.type !== CASH_PAYMENT_TYPE_ID
            ) {
                this.$set(
                    this.payments[index],
                    'amountReceived',
                    payment.toPay
                )
            }
        },
        calculateChange(payment) {
            // Existing (locked) payment lines keep the change recorded when they were saved.
            if ((payment.id || payment.allocationId) && payment.change != null) {
                return (parseFloat(payment.change) || 0).toFixed(2)
            }
            if (payment.amountReceived === null || payment.amountReceived === '') return ''
            if (this.showToPay) {
                const received = parseFloat(payment.amountReceived)
                const toPay = parseFloat(payment.toPay) || 0
                return Math.max(0, received - toPay).toFixed(2)
            }
            if (this.remainingToPay === 0) return ''
            const change = this.totalReceived - this.remainingToPay
            return Math.max(0, change).toFixed(2)
        },
        addPaymentLine() {
            if (this.noMethodsRemaining) return

            const usedTypes = this.payments.map(p => p.type)
            const cashAlreadyUsed = usedTypes.includes(CASH_PAYMENT_TYPE_ID)
            let defaultType = CASH_PAYMENT_TYPE_ID
            if (cashAlreadyUsed) {
                const firstAvailable = this.getPaymentTypeListAll.find(m => !usedTypes.includes(m.id))
                defaultType = firstAvailable ? firstAvailable.id : null
            }
            const line = { type: defaultType, amountReceived: null, isAgentPayment: false }
            if (this.showToPay) line.toPay = null
            this.payments.push(line)
        },
        isAgentPayment(payment) {
            return payment.type === AGENT_DEPOSIT_PAYMENT_TYPE_ID || !!payment.isAgentPayment
        },
        isAgentCheckboxDisabled(payment) {
            return payment.type === AGENT_DEPOSIT_PAYMENT_TYPE_ID || !!(payment.id || payment.allocationId)
        },
        onAgentPaymentChange(index, checked) {
            this.$set(this.payments[index], 'isAgentPayment', checked)
        },
        isPaymentDeletable(payment) {
            return payment.canBeDeleted !== false
        },
        removePaymentLine(index) {
            if (!this.isPaymentDeletable(this.payments[index])) return
            this.payments.splice(index, 1)
            if (this.payments.length === 0 && !this.allowEmpty) {
                this.addPaymentLine();
            }
        },
    },
}
</script>

<style lang="scss">
    .pay-in__col-method .vs__dropdown-toggle {
        padding: 4px 0;
        cursor: pointer;
    }

    .pay-in__col-method .vs__selected-options {
        min-height: 2rem;
        padding-left: 8px;
    }

    .pay-in__col-method .vs__selected {
        padding-left: 0;
    }

    .pay-in__col-method .vs__dropdown-option {
        padding: 3px 8px;
    }
</style>

<style lang="scss" scoped>
    @import '../../../assets/scss/_variables.scss';

    $disabled-opacity: 0.4;

    .pay-in__required {
        color: red;
    }

    .pay-in__payment-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        &--header label {
            font-size: $font-size-small;
            margin-bottom: 0;
            padding-left: 0.5rem;
        }
    }

    .pay-in__col-method {
        flex: 0.9;
        min-width: 0;
    }
    .pay-in__col-topay    { flex: 0.7; min-width: 0; }
    .pay-in__col-received { flex: 0.7; min-width: 0; }
    .pay-in__col-change   { flex: 0.7; min-width: 0; }
    .pay-in__col-agent    { width: 2.5rem; flex-shrink: 0; text-align: center; }
    .pay-in__col-delete   { width: 1.5rem; flex-shrink: 0; text-align: center; }

    .pay-in__input-group {
        position: relative;

        input {
            width: 100%;
            padding-right: 1.4rem;
            text-align: left;
        }

        .pay-in__input-suffix {
            position: absolute;
            right: 0.4rem;
            top: 50%;
            transform: translateY(-50%);
            color: $dark;
            font-size: $font-size-small;
            pointer-events: none;
        }
    }

    .pay-in__input--covered {
        border-color: $red-bright !important;
        outline-color: $red-bright;
    }

    .pay-in__select-wrapper--covered {
        border-radius: 4px;
        box-shadow: 0 0 0 1px $red-bright;
    }

    .pay-in__delete-icon {
        font-size: 0.95rem;
    }

    .pay-in__add-method {
        color: $darkest;
        font-weight: 500;
        cursor: pointer;
        font-size: $font-size-small;

        .add-circle {
            transform: scale(0.75);
        }

        &--disabled {
            opacity: $disabled-opacity;
            pointer-events: none;
        }
    }
</style>


// WEBPACK FOOTER //
// PaymentMethodForm.vue?54d5713e